import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClientHeader from "../../components/Client/ClientHeader";
import Footer from "../../components/Client/Footer";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { fetchAddresses } from "../../api/addressService";
import { fetchCartDeliveryQuote } from "../../api/deliveryPricingService";
import { validateCoupon, validateClientCoupon } from "../../api/couponService";
import "../../components/Client/clientTheme.css";
import "./Cart.css";
import { useLanguage } from "../../context/LanguageContext";
import { localizedItemProductName } from "../../utils/localizedName";

function getErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  return fallback;
}

export default function Cart() {
  const { t } = useTranslation(["cart", "common"]);
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { isAuthenticated, openLoginModal } = useAuth();
  const { items, updateItem, removeItem } = useCart();

  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressLoading, setAddressLoading] = useState(true);
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [deliveryQuoteLoading, setDeliveryQuoteLoading] = useState(false);
  const [companySelections, setCompanySelections] = useState({});
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [selectedCouponStoreId, setSelectedCouponStoreId] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setAddressLoading(false);
      return;
    }
    setAddressLoading(true);
    fetchAddresses()
      .then((addresses) => {
        setSelectedAddress(addresses?.find((a) => a.isDefault) || addresses?.[0] || null);
      })
      .catch(() => setSelectedAddress(null))
      .finally(() => setAddressLoading(false));
  }, [isAuthenticated]);

  useEffect(() => {
    setAppliedCoupon(null);
    setCouponError(null);
    setSelectedCouponStoreId(null);
    setCompanySelections({});

    if (items.length === 0 || !selectedAddress) {
      setDeliveryQuote(null);
      return;
    }

    let cancelled = false;
    setDeliveryQuoteLoading(true);
    fetchCartDeliveryQuote(selectedAddress.addressId)
      .then((quote) => {
        if (!cancelled) setDeliveryQuote(quote);
      })
      .catch(() => {
        if (!cancelled) setDeliveryQuote(null);
      })
      .finally(() => {
        if (!cancelled) setDeliveryQuoteLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, selectedAddress?.addressId]);

  const selectedCompanyFor = (storeId, storeQuote) =>
    companySelections[storeId] ?? storeQuote?.companies?.[0]?.deliveryCompanyUserId ?? null;

  const priceForStoreQuote = (storeQuote) => {
    if (!storeQuote) return 0;
    if (storeQuote.isPrime && storeQuote.companies?.length) {
      const companyId = selectedCompanyFor(storeQuote.storeId, storeQuote);
      const company = storeQuote.companies.find((c) => c.deliveryCompanyUserId === companyId);
      return company?.price ?? 0;
    }
    return storeQuote.price ?? 0;
  };

  const subtotal = items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
  const deliveryFee = (deliveryQuote?.stores ?? [])
    .filter((s) => s.isDeliverable)
    .reduce((sum, s) => sum + priceForStoreQuote(s), 0);

  const cartStoreIds = [...new Set(items.map((item) => item.storeId))];
  const storeOptions = cartStoreIds.map((id) => ({
    storeId: id,
    storeName: items.find((item) => item.storeId === id)?.storeName ?? "",
  }));
  const activeCouponStoreId = cartStoreIds.length === 1 ? cartStoreIds[0] : selectedCouponStoreId;
  const activeStoreSubtotal =
    activeCouponStoreId != null
      ? items.filter((item) => item.storeId === activeCouponStoreId).reduce((sum, item) => sum + item.unitPrice * item.quantity, 0)
      : 0;
  const storeDeliveryFee =
    activeCouponStoreId != null
      ? priceForStoreQuote(deliveryQuote?.stores?.find((s) => s.storeId === activeCouponStoreId))
      : 0;

  const discountAmount = appliedCoupon?.discountAmount ?? 0;
  const total = Math.max(0, subtotal + deliveryFee - discountAmount);

  function handleSelectCouponStore(storeId) {
    if (storeId === selectedCouponStoreId) return;
    setSelectedCouponStoreId(storeId);
    setAppliedCoupon(null);
    setCouponError(null);
    setCouponCode("");
  }

  async function handleApplyCoupon() {
    const trimmedCode = couponCode.trim();
    if (!trimmedCode || couponLoading) return;

    setCouponLoading(true);
    setCouponError(null);
    try {
      if (activeCouponStoreId != null) {
        const result = await validateCoupon({
          code: trimmedCode,
          storeId: activeCouponStoreId,
          orderAmount: activeStoreSubtotal,
          deliveryFee: storeDeliveryFee,
          addressId: selectedAddress?.addressId,
        });

        if (result.isValid) {
          const appliedStore = storeOptions.find((s) => s.storeId === activeCouponStoreId);
          setAppliedCoupon({
            scope: "store",
            code: trimmedCode,
            discountAmount: result.discountAmount,
            storeId: activeCouponStoreId,
            storeName: appliedStore?.storeName ?? "",
          });
          return;
        }
      }

      const clientResult = await validateClientCoupon({ code: trimmedCode, orderAmount: subtotal });
      if (clientResult.isValid) {
        setAppliedCoupon({ scope: "client", code: trimmedCode, discountAmount: clientResult.discountAmount });
      } else {
        setAppliedCoupon(null);
        setCouponError(clientResult.message);
      }
    } catch (err) {
      setAppliedCoupon(null);
      setCouponError(getErrorMessage(err, t("couponCheckError")));
    } finally {
      setCouponLoading(false);
    }
  }

  function handleRemoveCoupon() {
    setAppliedCoupon(null);
    setCouponError(null);
    setCouponCode("");
  }

  function handleCheckout() {
    if (items.length === 0 || !selectedAddress) return;

    navigate("/checkout", {
      state: {
        items,
        selectedAddress,
        subtotal,
        deliveryFee,
        discountAmount,
        total,
        couponCode: appliedCoupon?.code,
        deliveryQuote,
        companySelections,
      },
    });
  }

  const addressLine = selectedAddress
    ? [selectedAddress.street, selectedAddress.buildingNumber, selectedAddress.cityNameEnglish || selectedAddress.city]
        .filter(Boolean)
        .join(", ")
    : null;

  return (
    <div className="client-cart-page">
      <ClientHeader />

      <div className="client-cart-content">
        <h1 className="client-cart-title">{t("cartTitle")}</h1>

        {!isAuthenticated ? (
          <div className="client-cart-empty">
            <p>{t("loginPromptCart")}</p>
            <button type="button" className="client-cart-login-button" onClick={openLoginModal}>
              {t("common:logIn")}
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="client-cart-empty">
            <p>{t("emptyCartMessage")}</p>
            <Link to="/products" className="client-cart-browse-link">
              {t("browseProductsLink")}
            </Link>
          </div>
        ) : (
          <div className="client-cart-layout">
            <div className="client-cart-list">
              {items.map((item) => (
                <div className="client-cart-row" key={item.cartItemId}>
                  <Link to={`/product/${item.productId}`} className="client-cart-row-image-link">
                    {item.productImageUrl ? (
                      <img src={item.productImageUrl} alt={localizedItemProductName(language, item)} className="client-cart-row-image" />
                    ) : (
                      <div className="client-cart-row-image client-cart-row-image-placeholder" />
                    )}
                  </Link>

                  <div className="client-cart-row-info">
                    <Link to={`/product/${item.productId}`} className="client-cart-row-name">
                      {localizedItemProductName(language, item)}
                    </Link>
                    <div className="client-cart-row-store">{item.storeName}</div>
                    {item.variantName && <div className="client-cart-row-variant">{item.variantName}</div>}
                    <div className="client-cart-row-price">{item.unitPrice.toFixed(2)} ₪</div>
                  </div>

                  <div className="client-cart-row-controls">
                    <div className="client-cart-qty-stepper">
                      <button
                        type="button"
                        onClick={() => updateItem(item.cartItemId, Math.max(1, item.quantity - 1))}
                        disabled={item.quantity <= 1}
                      >
                        −
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() => updateItem(item.cartItemId, item.quantity + 1)}
                        disabled={item.quantity >= item.stockQuantity}
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      className="client-cart-row-remove-button"
                      onClick={() => removeItem(item.cartItemId)}
                    >
                      {t("common:remove")}
                    </button>
                  </div>

                  <div className="client-cart-row-subtotal">{(item.unitPrice * item.quantity).toFixed(2)} ₪</div>
                </div>
              ))}
            </div>

            <div className="client-cart-summary">
              {!addressLoading && (
                <Link to="/addresses" className="client-cart-address-card">
                  <div className="client-cart-address-info">
                    <span className="client-cart-address-label">{t("deliverTo")}</span>
                    {selectedAddress ? (
                      <>
                        <span className="client-cart-address-title">{selectedAddress.title || t("addressFallback")}</span>
                        <span className="client-cart-address-line">{addressLine}</span>
                      </>
                    ) : (
                      <span className="client-cart-address-missing">{t("noAddressSet")}</span>
                    )}
                  </div>
                  <span className="client-cart-address-change">{selectedAddress ? t("changeAddress") : t("common:add")}</span>
                </Link>
              )}

              {deliveryQuote?.stores
                ?.filter((s) => s.isDeliverable && s.isPrime && s.companies?.length > 1)
                .map((s) => {
                  const selectedCompanyId = selectedCompanyFor(s.storeId, s);
                  return (
                    <div className="client-cart-company-block" key={s.storeId}>
                      <span className="client-cart-company-label">{t("chooseDeliveryCompanyLabel", { store: s.storeName })}</span>
                      <div className="client-cart-company-chips">
                        {s.companies.map((company) => {
                          const selected = company.deliveryCompanyUserId === selectedCompanyId;
                          return (
                            <button
                              type="button"
                              key={company.deliveryCompanyUserId}
                              className={`client-cart-company-chip ${selected ? "active" : ""}`}
                              onClick={() =>
                                setCompanySelections((prev) => ({ ...prev, [s.storeId]: company.deliveryCompanyUserId }))
                              }
                            >
                              <span>{company.companyName}</span>
                              <span>{company.price.toFixed(2)} ₪</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

              <div className="client-cart-coupon-section">
                {storeOptions.length > 1 && !appliedCoupon && (
                  <div className="client-cart-coupon-store-select">
                    <span className="client-cart-coupon-store-label">{t("couponSelectStoreLabel")}</span>
                    <div className="client-cart-coupon-store-chips">
                      {storeOptions.map((store) => (
                        <button
                          type="button"
                          key={store.storeId}
                          className={`client-cart-coupon-store-chip ${activeCouponStoreId === store.storeId ? "active" : ""}`}
                          onClick={() => handleSelectCouponStore(store.storeId)}
                        >
                          {store.storeName}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {appliedCoupon ? (
                  <div className="client-cart-coupon-applied">
                    <span>
                      {appliedCoupon.scope === "store" && storeOptions.length > 1
                        ? t("couponAppliedTextForStore", { code: appliedCoupon.code, store: appliedCoupon.storeName })
                        : t("couponAppliedText", { code: appliedCoupon.code })}
                    </span>
                    <button type="button" className="client-cart-coupon-remove" onClick={handleRemoveCoupon}>
                      {t("common:remove")}
                    </button>
                  </div>
                ) : (
                  <div className="client-cart-coupon-input-row">
                    <input
                      type="text"
                      className="client-cart-coupon-input"
                      placeholder={t("couponCodePlaceholder")}
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        setCouponError(null);
                      }}
                      disabled={couponLoading}
                    />
                    <button
                      type="button"
                      className="client-cart-coupon-apply-button"
                      onClick={handleApplyCoupon}
                      disabled={!couponCode.trim() || couponLoading}
                    >
                      {t("common:apply")}
                    </button>
                  </div>
                )}
                {couponError && <p className="client-cart-coupon-error">{couponError}</p>}
              </div>

              <div className="client-cart-summary-row">
                <span>{t("subtotalLabel")}</span>
                <span>{subtotal.toFixed(2)} ₪</span>
              </div>
              <div className="client-cart-summary-row">
                <span>{t("deliveryLabel")}</span>
                {deliveryQuoteLoading ? (
                  <span className="client-cart-summary-muted">{t("common:loading")}</span>
                ) : !selectedAddress ? (
                  <span className="client-cart-summary-muted">{t("addAnAddress")}</span>
                ) : deliveryQuote && deliveryQuote.stores.length > 0 ? (
                  <span>{deliveryFee.toFixed(2)} ₪</span>
                ) : (
                  <span className="client-cart-summary-muted">{t("unavailable")}</span>
                )}
              </div>
              {discountAmount > 0 && (
                <div className="client-cart-summary-row">
                  <span>{t("discountLabel")}</span>
                  <span className="client-cart-summary-discount">-{discountAmount.toFixed(2)} ₪</span>
                </div>
              )}
              <div className="client-cart-summary-row total">
                <span>{t("common:total")}</span>
                <span>{total.toFixed(2)} ₪</span>
              </div>

              <button
                type="button"
                className="client-cart-checkout-button"
                onClick={handleCheckout}
                disabled={!selectedAddress}
              >
                {t("checkoutButton")}
              </button>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
